import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import useFocusTrap from '../hooks/useFocusTrap';
import { useParams } from 'react-router-dom';
import { menuAPI, waiterCallAPI, publicAPI, getErrorMessage } from '../services/api';
import { ALLERGENS, ALLERGEN_MAP } from '../constants/allergens';
import CookieConsentBanner from '../components/CookieConsentBanner';
import { useLanguage } from '../context/LanguageContext';
import { getThemeById, themeToCSSVars } from '../data/qrMenuThemes';
import QRCartConfirmModal from '../components/qrmenu/QRCartConfirmModal';
import { Skeleton } from '../components/Skeleton';

// ─── Cart helpers ─────────────────────────────────────────────────────────────

// IMP-11: Sepet localStorage'da persist — sayfa yenilemesinde veri kaybolmaz.
// Masa başı anahtar kullanılır ki farklı masalardaki QR oturumları karışmasın.
function cartStorageKey() {
  const match = typeof window !== 'undefined' ? window.location.pathname.match(/\/menu\/([^/?#]+)/) : null;
  return `restaflex_qr_cart_${match ? match[1] : 'default'}`;
}

function loadCartFromStorage() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(cartStorageKey());
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function useCart() {
  const [cart, setCart] = useState(loadCartFromStorage); // [{ item, variant, quantity, note }]

  // Her değişiklikte localStorage'a yaz
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(cartStorageKey(), JSON.stringify(cart));
    } catch {
      /* quota dolu veya private mode — sessiz geç */
    }
  }, [cart]);

  const addItem = useCallback((item, variant = null) => {
    setCart(prev => {
      const key = `${item.id}_${variant?.id || 'base'}`;
      const existing = prev.find(c => `${c.item.id}_${c.variant?.id || 'base'}` === key);
      if (existing) {
        return prev.map(c =>
          `${c.item.id}_${c.variant?.id || 'base'}` === key
            ? { ...c, quantity: c.quantity + 1 }
            : c
        );
      }
      return [...prev, { item, variant, quantity: 1, note: '' }];
    });
  }, []);

  const removeItem = useCallback((itemId, variantId) => {
    const key = `${itemId}_${variantId || 'base'}`;
    setCart(prev => prev.filter(c => `${c.item.id}_${c.variant?.id || 'base'}` !== key));
  }, []);

  const updateQuantity = useCallback((itemId, variantId, delta) => {
    const key = `${itemId}_${variantId || 'base'}`;
    setCart(prev =>
      prev
        .map(c =>
          `${c.item.id}_${c.variant?.id || 'base'}` === key
            ? { ...c, quantity: c.quantity + delta }
            : c
        )
        .filter(c => c.quantity > 0)
    );
  }, []);

  const updateNote = useCallback((itemId, variantId, note) => {
    const key = `${itemId}_${variantId || 'base'}`;
    setCart(prev =>
      prev.map(c =>
        `${c.item.id}_${c.variant?.id || 'base'}` === key ? { ...c, note } : c
      )
    );
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const totalItems = cart.reduce((s, c) => s + c.quantity, 0);
  const totalPrice = cart.reduce((s, c) => {
    const base = parseFloat(c.item.price || 0);
    const modifier = parseFloat(c.variant?.price_modifier || 0);
    return s + (base + modifier) * c.quantity;
  }, 0);

  return { cart, addItem, removeItem, updateQuantity, updateNote, clearCart, totalItems, totalPrice };
}

// ─── Main component ───────────────────────────────────────────────────────────

// RTL-safe currency formatter — AR locale'inde Arapça rakam + sembol yönü Intl ile doğru
// otomatik halloluyor (₺ → AR'de sayının sağında). LTR locale'lerde alışılmış '₺X.XX' davranışı.
function formatPrice(amount, lang) {
  const num = parseFloat(amount || 0);
  try {
    return new Intl.NumberFormat(lang === 'ar' ? 'ar' : lang === 'en' ? 'en-US' : 'tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `₺${num.toFixed(2)}`;
  }
}

export default function QRMenu() {
  const { t, isRTL, lang } = useLanguage();
  const { tableId } = useParams();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [excludedAllergens, setExcludedAllergens] = useState([]);
  const [showAllergenFilter, setShowAllergenFilter] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restaurantId, setRestaurantId] = useState(null);
  const [prepTime, setPrepTime] = useState(null); // IMP-10: tahmini hazırlık süresi (dk)
  const [resolvedTableId, setResolvedTableId] = useState(null);
  const [menuSettings, setMenuSettings] = useState({
    name: '',
    menu_title: null,
    menu_cover_url: null,
    menu_accent_color: '#f97316',
    menu_footer_text: null,
    qr_theme_id: 'classic-cream',
    qr_show_images: true,
    qr_show_descriptions: true,
    qr_show_prices: true,
    qr_layout: 'grid',
    qr_enable_cart: true,
    qr_enable_waiter_call: true,
    qr_custom_primary: null,
    qr_custom_secondary: null,
    qr_logo_url: null,
    qr_logo_size: 'md',
    qr_welcome_message: null,
    qr_min_order: 0,
  });
  const [qrCartModalOpen, setQrCartModalOpen] = useState(false);
  const [confirmClearCart, setConfirmClearCart] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.sessionStorage.getItem('qrmenu_welcome_dismissed') === '1';
    } catch { return false; }
  });
  const [selectedItem, setSelectedItem] = useState(null);

  // Cart
  const { cart, addItem, updateQuantity, clearCart, totalItems, totalPrice } = useCart();

  // Sipariş akışı — sadece QR ile garsona gönder yolu
  const [step, setStep] = useState('menu'); // 'menu' | 'done'
  const [completedOrder, setCompletedOrder] = useState(null);

  const catBarRef = useRef(null);
  const categoryRefs = useRef({});

  // Waiter call state
  const [waiterCallState, setWaiterCallState] = useState('idle'); // 'idle' | 'calling' | 'done' | 'error'
  const [waiterCallMsg, setWaiterCallMsg] = useState('');
  const waiterCallTimerRef = useRef(null);

  const handleCallWaiter = useCallback(async () => {
    if (waiterCallState === 'calling' || waiterCallState === 'done') return;
    if (!restaurantId) return;

    setWaiterCallState('calling');
    try {
      await waiterCallAPI.call({
        restaurant_id: parseInt(restaurantId),
        table_id: resolvedTableId || null,
      });
      setWaiterCallState('done');
      setWaiterCallMsg(t('qrmenuWaiterCalled'));
      clearTimeout(waiterCallTimerRef.current);
      waiterCallTimerRef.current = setTimeout(() => setWaiterCallState('idle'), 30000);
    } catch (err) {
      const msg = getErrorMessage(err, t('qrmenuWaiterCallFailed'));
      setWaiterCallState('error');
      setWaiterCallMsg(msg);
      clearTimeout(waiterCallTimerRef.current);
      waiterCallTimerRef.current = setTimeout(() => setWaiterCallState('idle'), 5000);
    }
  }, [restaurantId, resolvedTableId, waiterCallState, t]);

  // Load restaurant info
  useEffect(() => {
    const isNumeric = tableId && /^\d+$/.test(tableId);
    const isGeneral = !tableId || tableId === 'general';
    const qs = new URLSearchParams(window.location.search);

    let params = {};
    if (!isNumeric && !isGeneral) {
      params = { slug: tableId };
    } else if (qs.get('restaurant_id')) {
      params = { restaurant_id: qs.get('restaurant_id') };
    }

    Promise.all([
      menuAPI.getPublicMenu(params),
      menuAPI.getPublicSettings(params),
    ])
      .then(([menuRes, settingsRes]) => {
        const data = menuRes.data.data || {};
        const cats = data.categories || [];
        const allItems = data.items || [];
        const rid = data.restaurant_id || qs.get('restaurant_id') || null;
        setRestaurantId(rid);
        setResolvedTableId(isNumeric ? parseInt(tableId) : null);
        setCategories(cats);
        setItems(allItems);
        if (cats.length > 0) setActiveCategory(cats[0].id);
        if (settingsRes.data.data) setMenuSettings(settingsRes.data.data);
        // IMP-10: tahmini hazırlık süresini public endpoint'ten çek
        if (rid) {
          publicAPI.getPrepTimeEstimate(parseInt(rid))
            .then(r => setPrepTime(r.data.data?.avg_minutes || 15))
            .catch(() => setPrepTime(15));
        }
      })
      .catch(() => setError(t('qrmenuLoadFailed')))
      .finally(() => setLoading(false));
  }, [tableId, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tema sistemi — qr_theme_id'den preset al, custom override uygula
  const themePreset = getThemeById(menuSettings.qr_theme_id);
  const themeOverrides = {};
  if (menuSettings.qr_custom_primary) themeOverrides.primary = menuSettings.qr_custom_primary;
  if (menuSettings.qr_custom_secondary) themeOverrides.secondary = menuSettings.qr_custom_secondary;
  if (menuSettings.qr_custom_font) themeOverrides.font = menuSettings.qr_custom_font;
  const themeVars = themeToCSSVars(themePreset, themeOverrides);
  const accent = themeOverrides.primary || menuSettings.menu_accent_color || themePreset.primary || '#f97316';

  // Tema fontlarını dinamik yükle (Google Fonts) — heading (font) + body (bodyFont) ayrı
  // Custom font override sadece heading'i etkiler; body font okunabilir kalır.
  useEffect(() => {
    const headingFont = menuSettings.qr_custom_font || themePreset?.font;
    const bodyFont = themePreset?.bodyFont || themePreset?.font;
    const fonts = Array.from(new Set([headingFont, bodyFont].filter(Boolean)));
    fonts.forEach((fontName) => {
      const safeName = fontName.replace(/\s+/g, '+');
      const id = `qm-font-${safeName}`;
      if (document.getElementById(id)) return;
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${safeName}:wght@400;500;600;700&display=swap`;
      document.head.appendChild(link);
    });
  }, [themePreset?.font, themePreset?.bodyFont, menuSettings.qr_custom_font]);
  const title = menuSettings.menu_title || menuSettings.name || t('qrmenuDefaultTitle');

  const itemPassesAllergenFilter = (item) => {
    if (excludedAllergens.length === 0) return true;
    const als = item.allergens
      ? (typeof item.allergens === 'string' ? JSON.parse(item.allergens) : item.allergens)
      : [];
    return !excludedAllergens.some(key => als.includes(key));
  };

  const filteredItems = (activeCategory === 'all' ? items : items.filter(item => item.category_id === activeCategory))
    .filter(itemPassesAllergenFilter);

  const toggleExcludedAllergen = (key) => {
    setExcludedAllergens(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const scrollCatIntoView = (id) => {
    const el = catBarRef.current?.querySelector(`[data-cat="${id}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  const handleCatClick = (id) => {
    setActiveCategory(id);
    scrollCatIntoView(id);
    const section = categoryRefs.current[id];
    if (section) {
      const offset = 110;
      const top = section.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  // ── Screens ──────────────────────────────────────────────────────────────────

  if (loading) return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      lang={lang}
      className="min-h-screen"
      style={{
        ...themeVars,
        background: `linear-gradient(135deg, ${accent}15, ${accent}05)`,
        fontFamily: 'var(--qm-font, system-ui)',
      }}
      role="status"
      aria-label={t('loading') || 'Yükleniyor...'}
    >
      {/* Header placeholder: logo + title + chip row */}
      <div className="px-4 pt-6 pb-4 flex flex-col items-center gap-3">
        <Skeleton className="h-16 w-16 rounded-full" />
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-3 w-28" />
      </div>
      <div className="px-4 mb-4 flex gap-2 overflow-hidden">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-20 rounded-full flex-shrink-0" />
        ))}
      </div>
      {/* Menu item card skeletons (6 placeholders) */}
      <div className="px-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-lg p-3 bg-white/60 dark:bg-gray-800/60 flex gap-3" aria-hidden="true">
            <Skeleton className="h-20 w-20 rounded-md flex-shrink-0" />
            <div className="flex-1 space-y-2 py-1">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-4 w-16 mt-2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (error) return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      lang={lang}
      role="alert"
      aria-live="assertive"
      className="min-h-screen flex flex-col items-center justify-center gap-3"
      style={{
        ...themeVars,
        background: 'var(--qm-bg, #f9fafb)',
        color: 'var(--qm-text, #111827)',
        fontFamily: 'var(--qm-font, system-ui)',
      }}
    >
      <div className="text-5xl" aria-hidden="true">😕</div>
      <p className="text-sm" style={{ color: 'var(--qm-text-muted, #6b7280)' }}>{error}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-2 px-4 py-2 rounded-lg text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
        style={{ background: 'var(--qm-primary, #f97316)' }}
      >
        {t('retry') || 'Tekrar Dene'}
      </button>
    </div>
  );

  // Order confirmed screen
  if (step === 'done') return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      lang={lang}
      role="status"
      aria-live="polite"
      className="min-h-screen flex flex-col items-center justify-center gap-5 px-6 text-center"
      style={{
        ...themeVars,
        background: `linear-gradient(135deg, ${accent}10, var(--qm-bg, #fff))`,
        fontFamily: 'var(--qm-font, system-ui)',
      }}
    >
      <div className="text-7xl" aria-hidden="true">✅</div>
      <h1 className="text-2xl font-black" style={{ color: 'var(--qm-text, #1f2937)', fontFamily: 'var(--qm-font-heading, var(--qm-font, inherit))' }}>{t('qrmenuOrderReceived')}</h1>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--qm-text-muted, #6b7280)' }}>
        {t('qrmenuOrderSentToKitchen')}
      </p>
      {/* IMP-10: Tahmini hazırlık süresi */}
      {prepTime != null && (
        <div className="rounded-full px-4 py-2 shadow text-sm font-semibold flex items-center gap-2" style={{ background: 'var(--qm-surface, #fff)', color: accent }}>
          <span>⏱️</span>
          <span>{t('qrmenuApproxReadyIn')} {prepTime} {t('qrmenuMinutes')}</span>
        </div>
      )}
      {completedOrder && (
        <div className="rounded-2xl shadow p-5 w-full max-w-xs text-left space-y-2" style={{ background: 'var(--qm-surface, #fff)' }}>
          <div className="flex justify-between text-sm" style={{ color: 'var(--qm-text-muted, #6b7280)' }}>
            <span>{t('qrmenuOrderNo')}</span>
            <span className="font-bold">#{completedOrder.order_id}</span>
          </div>
          <div className="flex justify-between text-sm" style={{ color: 'var(--qm-text-muted, #6b7280)' }}>
            <span>{t('total')}</span>
            <span className="font-bold" style={{ color: accent }}>
              {formatPrice(completedOrder.total_amount, lang)}
            </span>
          </div>
        </div>
      )}
      <button
        onClick={() => setStep('menu')}
        className="mt-2 px-6 py-3 rounded-2xl text-sm font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
        style={{ backgroundColor: accent }}
      >
        {t('qrmenuBackToMenu')}
      </button>
    </div>
  );

  // ── Menu screen ───────────────────────────────────────────────────────────────
  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      lang={lang}
      className="min-h-screen"
      style={{
        ...themeVars,
        background: 'var(--qm-bg, #f9fafb)',
        color: 'var(--qm-text, #111827)',
        fontFamily: 'var(--qm-font, system-ui, sans-serif)',
      }}
    >
      {/* Welcome banner — tema'dan gelen karşılama mesajı (slide-in, kapatılabilir) */}
      {menuSettings.qr_welcome_message && !welcomeDismissed && (
        <div
          className="relative px-10 py-2.5 text-center text-sm font-semibold qm-welcome-banner"
          style={{ background: 'var(--qm-primary)', color: '#fff', fontFamily: 'var(--qm-font, inherit)' }}
        >
          <style>{`
            @keyframes qm-welcome-slide { from { transform: translateY(-100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            .qm-welcome-banner { animation: qm-welcome-slide 0.5s cubic-bezier(0.16, 1, 0.3, 1) both; }
            @media (prefers-reduced-motion: reduce) { .qm-welcome-banner { animation: none; } }
          `}</style>
          {menuSettings.qr_welcome_message}
          <button
            type="button"
            onClick={() => {
              setWelcomeDismissed(true);
              try { window.sessionStorage.setItem('qrmenu_welcome_dismissed', '1'); } catch {}
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 text-white text-base leading-none flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1"
            aria-label={t('close') || 'Kapat'}
            title={t('close') || 'Kapat'}
          >
            ✕
          </button>
        </div>
      )}
      {/* Hero */}
      <div className="relative w-full" style={{ minHeight: menuSettings.menu_cover_url ? 220 : 120 }}>
        {menuSettings.menu_cover_url ? (
          <>
            <img
              src={menuSettings.menu_cover_url}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
              onError={e => { e.target.style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
          </>
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background: themePreset.secondary
                ? `linear-gradient(135deg, ${accent}, ${themePreset.secondary || accent + 'cc'})`
                : `linear-gradient(135deg, ${accent}, ${accent}cc)`
            }}
          />
        )}
        <div className="relative z-10 flex flex-col items-center justify-end pb-6 pt-10 px-4 text-center" style={{ minHeight: menuSettings.menu_cover_url ? 220 : 120 }}>
          {menuSettings.qr_logo_url && (
            <img
              src={menuSettings.qr_logo_url.startsWith('http') ? menuSettings.qr_logo_url : menuSettings.qr_logo_url}
              alt="Logo"
              className={`mb-3 object-contain drop-shadow-2xl bg-white/90 backdrop-blur-sm rounded-xl p-1.5 ${
                menuSettings.qr_logo_size === 'sm' ? 'w-12 h-12'
                : menuSettings.qr_logo_size === 'lg' ? 'w-24 h-24 sm:w-28 sm:h-28'
                : 'w-16 h-16 sm:w-20 sm:h-20'
              }`}
              onError={e => { e.target.style.display = 'none'; }}
            />
          )}
          <h1 className="text-2xl sm:text-3xl font-black text-white drop-shadow-lg tracking-tight" style={{ fontFamily: 'var(--qm-font-heading, var(--qm-font, inherit))' }}>{title}</h1>
          {resolvedTableId && (
            <span
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold text-white backdrop-blur-md shadow-lg"
              style={{ background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              <span aria-hidden="true">🪑</span>
              <span>{t('tableNo')} {resolvedTableId}</span>
            </span>
          )}
          <p className="text-white/80 text-[11px] mt-2 font-semibold tracking-[0.2em] uppercase">{t('qrmenuDigitalMenu')}</p>
        </div>
      </div>

      {/* Category sticky bar — tema renkleriyle uyumlu */}
      <div
        className="sticky top-0 z-20 backdrop-blur-sm border-b shadow-sm"
        style={{
          background: 'color-mix(in srgb, var(--qm-surface, #ffffff) 92%, transparent)',
          borderColor: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 15%, transparent)',
        }}
      >
        <div ref={catBarRef} className="flex overflow-x-auto px-3 py-3 gap-2 scrollbar-none" style={{ scrollbarWidth: 'none', fontFamily: 'var(--qm-font, inherit)' }}>
          {(() => {
            const totalCount = items.filter(itemPassesAllergenFilter).length;
            const allActive = activeCategory === 'all';
            return (
              <button
                data-cat="all"
                onClick={() => { setActiveCategory('all'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary,var(--qm-primary,#f97316))]"
                style={allActive
                  ? { backgroundColor: accent, color: '#fff' }
                  : { background: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 12%, transparent)', color: 'var(--qm-text-muted, #6b7280)' }}
              >
                <span>{t('qrmenuAll')}</span>
                {totalCount > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black"
                    style={allActive
                      ? { background: 'rgba(255,255,255,0.25)', color: '#fff' }
                      : { background: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 22%, transparent)', color: 'var(--qm-text-muted, #6b7280)' }}
                  >
                    {totalCount}
                  </span>
                )}
              </button>
            );
          })()}
          {categories.map(cat => {
            const catCount = items.filter(i => i.category_id === cat.id).filter(itemPassesAllergenFilter).length;
            if (catCount === 0) return null;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                data-cat={cat.id}
                onClick={() => handleCatClick(cat.id)}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary,var(--qm-primary,#f97316))]"
                style={isActive
                  ? { backgroundColor: accent, color: '#fff' }
                  : { background: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 12%, transparent)', color: 'var(--qm-text-muted, #6b7280)' }}
              >
                <span>{cat.name}</span>
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black"
                  style={isActive
                    ? { background: 'rgba(255,255,255,0.25)', color: '#fff' }
                    : { background: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 22%, transparent)', color: 'var(--qm-text-muted, #6b7280)' }}
                >
                  {catCount}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Allergen filter — sadece admin alerjen göstermeyi açtıysa */}
      {menuSettings.qr_show_allergens === true && (
      <div className="max-w-lg mx-auto px-3 pt-3" style={{ fontFamily: 'var(--qm-font, inherit)' }}>
        <button
          onClick={() => setShowAllergenFilter(f => !f)}
          aria-expanded={showAllergenFilter}
          className={`flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary,var(--qm-primary,#f97316))] ${
            excludedAllergens.length > 0
              ? 'bg-orange-50 border-orange-300 text-orange-700'
              : 'border-gray-200 text-gray-500'
          }`}
          style={excludedAllergens.length === 0 ? {
            background: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 8%, transparent)',
            borderColor: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 20%, transparent)',
            color: 'var(--qm-text-muted, #6b7280)',
          } : undefined}>
          <span>⚠️</span>
          <span>{t('qrmenuAllergenFilter')}</span>
          {excludedAllergens.length > 0 && (
            <span className="bg-orange-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-black">
              {excludedAllergens.length}
            </span>
          )}
          <span className="ml-1">{showAllergenFilter ? '▲' : '▼'}</span>
        </button>
        {showAllergenFilter && (
          <div className="mt-2 p-3 bg-orange-50 rounded-2xl border border-orange-100">
            <p className="text-xs text-orange-700 font-semibold mb-2">{t('qrmenuAllergenFilterHint')}</p>
            <div className="grid grid-cols-2 gap-1.5">
              {ALLERGENS.map(al => {
                const excluded = excludedAllergens.includes(al.key);
                return (
                  <button
                    key={al.key}
                    type="button"
                    onClick={() => toggleExcludedAllergen(al.key)}
                    className={`flex items-center gap-2 px-2.5 py-2 rounded-xl text-xs font-medium border transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[var(--color-primary,var(--qm-primary,#f97316))] ${
                      excluded
                        ? 'bg-red-100 border-red-400 text-red-700'
                        : 'bg-white border-gray-200 text-gray-600'
                    }`}>
                    <span>{al.icon}</span>
                    <span className="truncate">{al.label}</span>
                    {excluded && <span className="ml-auto">✕</span>}
                  </button>
                );
              })}
            </div>
            {excludedAllergens.length > 0 && (
              <button
                onClick={() => setExcludedAllergens([])}
                className="mt-2 text-xs text-orange-600 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-orange-500 rounded">
                {t('qrmenuClearFilter')}
              </button>
            )}
          </div>
        )}
      </div>
      )}

      {/* Menu content — layout: list (default), grid (2 col), magazine (1 col big) */}
      <div className="max-w-lg mx-auto px-3 py-4 pb-28">
        {(() => {
          const layout = menuSettings.qr_layout || 'list';
          const showImage = menuSettings.qr_show_images !== false;
          const showDesc = menuSettings.qr_show_descriptions !== false;
          const showPrice = menuSettings.qr_show_prices !== false;
          const showAllergens = menuSettings.qr_show_allergens === true; // opt-in
          const isGrid = layout === 'grid';
          const containerCls = isGrid ? 'grid grid-cols-2 gap-2.5' : layout === 'magazine' ? 'space-y-4' : 'space-y-2.5';
          const cardVariant = themePreset?.cardVariant || 'paper';
          const renderItem = (item) => (
            <MenuItem
              key={item.id}
              item={item}
              accent={accent}
              cart={cart}
              layout={layout}
              cardVariant={cardVariant}
              showImage={showImage}
              showDescription={showDesc}
              showPrice={showPrice}
              showAllergens={showAllergens}
              onDetail={() => setSelectedItem(item)}
              onAdd={() => item.variants?.length ? setSelectedItem(item) : addItem(item)}
              onIncrement={() => addItem(item)}
              onDecrement={() => updateQuantity(item.id, null, -1)}
            />
          );
          if (activeCategory === 'all') {
            return categories.map(cat => {
              const catItems = items.filter(i => i.category_id === cat.id).filter(itemPassesAllergenFilter);
              if (catItems.length === 0) return null;
              return (
                <div key={cat.id} ref={el => categoryRefs.current[cat.id] = el} className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="font-bold text-base" style={{ color: 'var(--qm-text, #111827)', fontFamily: 'var(--qm-font-heading, var(--qm-font, inherit))' }}>{cat.name}</h2>
                    <div className="flex-1 h-px" style={{ background: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 20%, transparent)' }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--qm-text-muted, #9ca3af)' }}>{catItems.length} {t('qrmenuItemsCount')}</span>
                  </div>
                  <div className={containerCls}>{catItems.map(renderItem)}</div>
                </div>
              );
            });
          }
          return (
            <div className={containerCls}>
              {filteredItems.length === 0 ? (
                <div
                  className="col-span-2 text-center py-16 text-sm"
                  style={{ color: 'var(--qm-text-muted, #9ca3af)', fontFamily: 'var(--qm-font, inherit)' }}
                >
                  <div className="text-4xl mb-2 opacity-50" aria-hidden="true">🍽️</div>
                  {t('qrmenuNoItemsInCategory')}
                </div>
              ) : filteredItems.map(renderItem)}
            </div>
          );
        })()}
      </div>

      {/* Cart bottom bar — sticky, premium polish (backdrop blur, subtle glow) */}
      {totalItems > 0 && (
        <div
          className="fixed bottom-0 inset-x-0 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-md border-t shadow-[0_-8px_24px_rgba(0,0,0,0.06)]"
          style={{
            background: 'color-mix(in srgb, var(--qm-surface, #ffffff) 90%, transparent)',
            borderColor: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 15%, transparent)',
          }}
        >
          <div className="max-w-lg mx-auto space-y-2">
            {/* Min order uyarısı — sepet altında ise */}
            {menuSettings.qr_min_order > 0 && totalPrice < menuSettings.qr_min_order && menuSettings.qr_enable_cart !== false && (
              <div
                className="rounded-xl px-3 py-2 text-xs font-semibold text-center"
                style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A' }}
              >
                ⚠ {t('qrMinOrderWarn') || 'Minimum sipariş'} {formatPrice(menuSettings.qr_min_order, lang)} ·
                {' '}
                <span style={{ color: '#7C2D12' }}>
                  {t('qrMinOrderMissing') || 'Kalan'}: {formatPrice(menuSettings.qr_min_order - totalPrice, lang)}
                </span>
              </div>
            )}
            {/* Min order karşılandı rozeti — sadece min_order > 0 ve karşılanmışsa */}
            {menuSettings.qr_min_order > 0 && totalPrice >= menuSettings.qr_min_order && menuSettings.qr_enable_cart !== false && (
              <div
                className="rounded-xl px-3 py-1.5 text-[11px] font-semibold text-center flex items-center justify-center gap-1.5"
                style={{ background: '#D1FAE5', color: '#065F46', border: '1px solid #6EE7B7' }}
              >
                <span aria-hidden="true">✓</span>
                {t('qrMinOrderMet') || 'Minimum sipariş karşılandı'}
              </div>
            )}
            {/* QR Sipariş — qr_enable_cart açıksa + min order karşılanıyor (masa garson tarafından atanır) */}
            {menuSettings.qr_enable_cart !== false && totalPrice >= (menuSettings.qr_min_order || 0) && (
              <button
                onClick={() => setQrCartModalOpen(true)}
                className="w-full flex items-center justify-between py-3 px-5 rounded-2xl text-white font-bold text-sm transition-all active:scale-[0.98] shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
                style={{ backgroundColor: accent, boxShadow: `0 6px 18px ${accent}4d` }}
              >
                <span className="flex items-center gap-2">
                  <span className="bg-white/25 rounded-full w-7 h-7 flex items-center justify-center text-xs font-black">{totalItems}</span>
                  <span aria-hidden="true">📱</span>
                </span>
                <span className="text-base">{t('qrCartSendToWaiter') || 'QR ile Garsona Gönder'}</span>
                <span className="text-base">{formatPrice(totalPrice, lang)}</span>
              </button>
            )}
            {/* QR sipariş kapalıysa info banner — garson çağırma yönlendirmesi */}
            {menuSettings.qr_enable_cart === false && (
              <div
                className="rounded-xl px-3 py-2 text-xs font-semibold text-center"
                style={{ background: '#E0F2FE', color: '#075985', border: '1px solid #7DD3FC' }}
              >
                <span aria-hidden="true">ℹ️</span> {t('qrCartDisabledInfo') || 'Bu restoran QR sipariş kabul etmiyor, lütfen garson çağırın.'}
              </div>
            )}
            {resolvedTableId && menuSettings.qr_enable_waiter_call !== false && <WaiterCallButton state={waiterCallState} msg={waiterCallMsg} onCall={handleCallWaiter} accent={accent} compact />}
            <div className="flex justify-center">
              {confirmClearCart ? (
                <div className="flex items-center gap-2 text-xs">
                  <span style={{ color: 'var(--qm-text-muted, #6b7280)' }}>
                    {t('clearCartConfirm') || 'Sepeti boşaltmak istediğinize emin misiniz?'}
                  </span>
                  <button
                    type="button"
                    onClick={() => { clearCart(); setConfirmClearCart(false); }}
                    className="font-semibold underline focus-visible:outline-none focus-visible:ring-2 rounded px-1"
                    style={{ color: accent }}
                  >
                    {t('confirmBtn') || 'Onayla'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmClearCart(false)}
                    className="font-semibold underline focus-visible:outline-none focus-visible:ring-2 rounded px-1"
                    style={{ color: 'var(--qm-text-muted, #6b7280)' }}
                  >
                    {t('cancel') || 'İptal'}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmClearCart(true)}
                  className="text-xs hover:underline transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 rounded px-1 py-0.5"
                  style={{ color: 'var(--qm-text-muted, #6b7280)' }}
                >
                  <span aria-hidden="true">🗑</span> {t('clearCartBtn') || 'Sepeti Temizle'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* QR Sipariş Onay Modal */}
      <QRCartConfirmModal
        open={qrCartModalOpen}
        onClose={() => setQrCartModalOpen(false)}
        restaurantId={restaurantId}
        tableId={resolvedTableId}
        cart={cart}
        totalPrice={totalPrice}
        themeColor={accent}
        t={t}
        onClaimed={(orderId) => {
          // Garson sepeti aldı — sepeti temizle, 'done' step'ine geç
          clearCart();
          setCompletedOrder({ id: orderId, total_amount: totalPrice, source: 'qr_online' });
          // Modal kendi içinde claimed gösterir; 2 sn sonra kapat
          setTimeout(() => {
            setQrCartModalOpen(false);
            setStep('done');
          }, 2000);
        }}
      />

      {/* Footer (only when cart is empty) */}
      {totalItems === 0 && (
        <div
          className="fixed bottom-0 inset-x-0 backdrop-blur-md border-t px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
          style={{
            background: 'color-mix(in srgb, var(--qm-surface, #ffffff) 90%, transparent)',
            borderColor: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 15%, transparent)',
          }}
        >
          <div className="max-w-lg mx-auto space-y-2">
            {menuSettings.qr_enable_cart !== false && (
              <p
                className="text-xs text-center"
                style={{ color: 'var(--qm-text-muted, #9ca3af)', fontFamily: 'var(--qm-font, inherit)' }}
              >
                <span aria-hidden="true">🛒</span>{' '}
                {t('qrCartEmpty') || 'Sepetiniz boş — menüden ürün ekleyin'}
              </p>
            )}
            {resolvedTableId ? (
              <WaiterCallButton state={waiterCallState} msg={waiterCallMsg} onCall={handleCallWaiter} accent={accent} />
            ) : (
              <p
                className="text-xs font-medium text-center"
                style={{ color: 'var(--qm-text-muted, #9ca3af)', fontFamily: 'var(--qm-font, inherit)' }}
              >
                {menuSettings.menu_footer_text || t('qrmenuFooterHint')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Item detail / add to cart modal */}
      {selectedItem && (
        <ItemModal
          item={selectedItem}
          accent={accent}
          cart={cart}
          onAdd={(item, variant) => { addItem(item, variant); setSelectedItem(null); }}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {/* KVKK Çerez Rızası */}
      <CookieConsentBanner restaurantId={restaurantId} accent={accent} />
    </div>
  );
}

// ─── WaiterCallButton ─────────────────────────────────────────────────────────

function WaiterCallButton({ state, msg, onCall, accent, compact }) {
  const { t } = useLanguage();
  if (state === 'done') return (
    <div role="status" aria-live="polite" className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-green-50 border border-green-200">
      <span className="text-green-600 text-base" aria-hidden="true">🔔</span>
      <span className="text-xs font-semibold text-green-700">{msg}</span>
    </div>
  );

  if (state === 'error') return (
    <div role="alert" aria-live="assertive" className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-red-50 border border-red-200">
      <span className="text-xs font-semibold text-red-600">{msg}</span>
    </div>
  );

  return (
    <button
      onClick={onCall}
      disabled={state === 'calling'}
      className={`w-full flex items-center justify-center gap-2 rounded-2xl font-semibold transition-all active:scale-[0.98] disabled:opacity-60 border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current ${compact ? 'py-2 text-xs' : 'py-3 text-sm'}`}
      style={{ borderColor: accent, color: accent, backgroundColor: `${accent}10` }}
    >
      <span className="text-base">{state === 'calling' ? '⏳' : '🔔'}</span>
      {state === 'calling' ? t('qrmenuCallingWaiter') : t('qrmenuCallWaiter')}
    </button>
  );
}

// ─── MenuItem card ────────────────────────────────────────────────────────────

// cardVariant → kart için ek className + inline style üretir.
// Her variant gerçekten farklı görsel kimlik verir; accent tema rengine bağlı kalır.
// Layout (grid/list/magazine) iskeleti BOZULMAZ — variant onun ÜZERİNE binmiş ek katmandır.
function getVariantDecor(variant, accent, layout) {
  const v = variant || 'paper';
  const isList = layout === 'list';
  // Hex -> rgb helper (accent gradient/glow için)
  const hexToRgb = (hex) => {
    const h = (hex || '#000000').replace('#', '');
    const n = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const num = parseInt(n.slice(0, 6) || '000000', 16);
    return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
  };
  const rgb = hexToRgb(accent);

  switch (v) {
    case 'neo-brutalist':
      return {
        className: isList ? '' : 'rounded-none',
        style: isList
          ? { borderLeft: `4px solid ${accent}`, borderBottomColor: '#000' }
          : { border: '3px solid #000', borderRadius: 0, boxShadow: `7px 7px 0 ${accent}` },
        badge: null,
        accent: { fontWeight: 900, letterSpacing: '0.02em' },
      };
    case 'glass':
      return {
        className: isList ? '' : 'backdrop-blur-md',
        style: isList
          ? { background: `rgba(${rgb}, 0.04)`, borderBottomColor: `rgba(${rgb}, 0.25)` }
          : { background: `rgba(255,255,255,0.18)`, border: `1px solid rgba(255,255,255,0.35)`, boxShadow: `0 8px 32px rgba(${rgb}, 0.18)` },
        badge: null,
        accent: { textShadow: `0 1px 2px rgba(0,0,0,0.08)` },
      };
    case 'gradient-glow':
      return {
        className: '',
        style: isList
          ? { background: `linear-gradient(90deg, rgba(${rgb}, 0.08), transparent 60%)`, borderBottomColor: `rgba(${rgb}, 0.3)` }
          : { background: `linear-gradient(135deg, rgba(${rgb}, 0.12) 0%, rgba(${rgb}, 0.03) 100%)`, border: `1px solid rgba(${rgb}, 0.25)`, boxShadow: `0 0 24px rgba(${rgb}, 0.28), 0 2px 8px rgba(0,0,0,0.06)` },
        badge: null,
        accent: { textShadow: `0 0 8px rgba(${rgb}, 0.4)` },
      };
    case 'editorial':
      return {
        className: '',
        style: isList
          ? { borderBottom: `3px double ${accent}`, paddingBottom: '0.75rem' }
          : { background: 'var(--qm-surface, #fff)', border: 'none', borderBottom: `4px solid ${accent}`, borderRadius: 0, boxShadow: '0 2px 0 rgba(0,0,0,0.04)' },
        badge: null,
        accent: { fontFamily: 'var(--qm-font-heading, serif)', fontStyle: 'italic' },
      };
    case 'minimalist-mono':
      return {
        className: '',
        style: isList
          ? { borderBottomColor: 'rgba(0,0,0,0.08)', borderBottomWidth: '1px' }
          : { background: 'var(--qm-surface, #fff)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 0, boxShadow: 'none' },
        badge: null,
        accent: { fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontWeight: 600, letterSpacing: '-0.01em' },
      };
    case 'paper':
      return {
        className: '',
        style: isList
          ? { borderBottomColor: 'rgba(120, 90, 60, 0.18)' }
          : { background: `linear-gradient(180deg, #fffdf7 0%, var(--qm-surface, #fff) 100%)`, border: '1px solid rgba(120, 90, 60, 0.18)', boxShadow: '0 1px 0 rgba(120, 90, 60, 0.08), 0 4px 12px rgba(120, 90, 60, 0.06)' },
        badge: { icon: '🌿', label: 'fresh', style: { background: accent, color: '#fff' } },
        accent: {},
      };
    case 'retro-pop':
      return {
        className: '',
        style: isList
          ? { borderBottomColor: `rgba(${rgb}, 0.35)`, borderBottomWidth: '2px' }
          : { background: 'var(--qm-surface, #fff)', border: `2px solid ${accent}`, borderRadius: 24, boxShadow: `0 4px 0 rgba(${rgb}, 0.25), 0 8px 20px rgba(0,0,0,0.08)` },
        badge: { icon: '★', label: 'pop', style: { background: '#fff', color: accent, border: `2px solid ${accent}` } },
        accent: { textShadow: `1px 1px 0 rgba(${rgb}, 0.15)`, fontWeight: 900 },
      };
    case 'art-deco':
      return {
        className: '',
        style: isList
          ? { borderBottom: `2px solid ${accent}`, position: 'relative' }
          : { background: 'var(--qm-surface, #1a1a1a)', border: `2px solid ${accent}`, borderRadius: 4, boxShadow: `inset 0 0 0 4px rgba(${rgb}, 0.08), 0 6px 18px rgba(0,0,0,0.25)` },
        badge: null,
        accent: { letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 },
      };
    default:
      return { className: '', style: {}, badge: null, accent: {} };
  }
}

function MenuItem({ item, accent, cart, onDetail, onAdd, onIncrement, onDecrement, layout = 'list', cardVariant = 'paper', showImage = true, showDescription = true, showPrice = true, showAllergens = true }) {
  const { t, lang } = useLanguage();
  const cartQty = cart.filter(c => c.item.id === item.id).reduce((s, c) => s + c.quantity, 0);
  const hasImage = showImage && !!item.image_url;
  const isGrid = layout === 'grid';
  const isMagazine = layout === 'magazine';

  // Allergen renderer (3 layout'ta paylaşılıyor)
  const renderAllergens = (limit = 6, sizeCls = 'text-sm') => {
    if (!showAllergens) return null;
    const als = item.allergens
      ? (typeof item.allergens === 'string' ? JSON.parse(item.allergens) : item.allergens)
      : [];
    if (!als.length) return null;
    return (
      <div className="flex flex-wrap gap-0.5">
        {als.slice(0, limit).map(key => {
          const a = ALLERGEN_MAP[key];
          return a ? <span key={key} title={a.label} className={`${sizeCls} leading-none`}>{a.icon}</span> : null;
        })}
        {als.length > limit && <span className="text-[10px]" style={{ color: 'var(--qm-text-muted, #9ca3af)' }}>+{als.length - limit}</span>}
      </div>
    );
  };

  const headingFont = 'var(--qm-font-heading, var(--qm-font, inherit))';
  const decor = getVariantDecor(cardVariant, accent, layout);
  const priceStyle = decor.accent || {};

  // ─── GRID: image üstte h-40 ZORUNLU + kart galeri (tema shadow + radius) ───
  if (isGrid) {
    return (
      <div
        className={`w-full overflow-hidden flex flex-col hover:scale-[1.01] transition-all relative ${decor.className}`}
        style={{
          background: 'var(--qm-surface, #fff)',
          borderRadius: 'var(--qm-radius, 0.75rem)',
          boxShadow: 'var(--qm-shadow, 0 4px 6px -1px rgba(0,0,0,0.1))',
          ...decor.style,
        }}
      >
        {decor.badge && (
          <span
            className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider shadow-sm"
            style={decor.badge.style}
            aria-hidden="true"
          >{decor.badge.icon}</span>
        )}
        <button onClick={onDetail} className="text-left active:opacity-90 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-current w-full">
          {hasImage ? (
            <div className="relative w-full h-40 overflow-hidden bg-gray-200 animate-pulse" style={{ background: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 10%, transparent)' }}>
              <img
                src={item.image_url}
                alt={item.name}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                onLoad={e => { e.currentTarget.parentElement.classList.remove('animate-pulse', 'bg-gray-200'); }}
                onError={e => { e.target.parentElement.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f9fafb;font-size:3rem">🍽️</div>`; }}
              />
            </div>
          ) : (
            <div
              className="w-full h-40 flex items-center justify-center text-4xl"
              style={{ background: `linear-gradient(135deg, ${accent}25, ${accent}08)`, color: accent }}
            >🍽️</div>
          )}
          <div className="px-3 py-3 flex flex-col gap-1.5">
            <h3 className="font-bold text-[15px] leading-snug line-clamp-1" style={{ color: 'var(--qm-text, #111827)', fontFamily: headingFont, ...(cardVariant === 'editorial' || cardVariant === 'art-deco' || cardVariant === 'retro-pop' ? priceStyle : {}) }}>{item.name}</h3>
            {showDescription && item.description && (
              <p className="text-xs line-clamp-2 leading-relaxed" style={{ color: 'var(--qm-text-muted, #6b7280)' }}>{item.description}</p>
            )}
            {renderAllergens(6, 'text-sm')}
            <div className="flex items-center justify-between mt-1">
              {item.category_name ? (
                <span className="text-[10px] font-semibold uppercase tracking-wide truncate" style={{ color: 'var(--qm-text-muted, #9ca3af)' }}>{item.category_name}</span>
              ) : <span />}
              {showPrice && (
                <span className="text-lg font-black shrink-0" style={{ color: accent, ...priceStyle }}>
                  {formatPrice(item.price, lang)}
                </span>
              )}
            </div>
          </div>
        </button>
        {/* Full-width primary buton (grid imzası) */}
        <div className="px-3 pb-3">
          {cartQty > 0 && !item.variants?.length ? (
            <div className="flex items-center justify-between w-full">
              <button onClick={onDecrement} aria-label={t('qrmenuDecrement') || 'Bir adet azalt'} className="w-10 h-10 rounded-full border-2 flex items-center justify-center text-lg font-bold active:scale-90 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current" style={{ borderColor: accent, color: accent }}><span aria-hidden="true">−</span></button>
              <span className="text-lg font-black" style={{ color: accent }} aria-live="polite">{cartQty}</span>
              <button onClick={onIncrement} aria-label={t('qrmenuIncrement') || 'Bir adet ekle'} className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-bold active:scale-90 transition-all shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current" style={{ backgroundColor: accent }}><span aria-hidden="true">+</span></button>
            </div>
          ) : (
            <button
              onClick={onAdd}
              aria-label={t('qrmenuAddToCart') || 'Ürünü sepete ekle'}
              className="w-full py-2.5 rounded-xl text-white font-bold text-sm shadow-md active:scale-[0.98] hover:shadow-lg transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
              style={{ backgroundColor: accent, boxShadow: `0 4px 12px ${accent}40` }}
            >
              + {t('qrmenuAdd') || 'Ekle'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── MAGAZINE: edge-to-edge görsel hero + alt overlay başlık/fiyat (border yok, dramatic) ───
  if (isMagazine) {
    return (
      <div
        className={`w-full overflow-hidden flex flex-col transition-all hover:-translate-y-0.5 relative ${decor.className}`}
        style={{
          background: 'var(--qm-surface, #fff)',
          borderRadius: 'var(--qm-radius, 1rem)',
          boxShadow: 'var(--qm-shadow, 0 10px 30px -8px rgba(0,0,0,0.18))',
          ...decor.style,
        }}
      >
        {decor.badge && (
          <span
            className="absolute top-3 left-3 z-20 px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider shadow-md"
            style={decor.badge.style}
            aria-hidden="true"
          >{decor.badge.icon}</span>
        )}
        <button onClick={onDetail} className="relative w-full text-left active:opacity-95 transition-all block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-current">
          {hasImage ? (
            <div className="relative w-full h-52 overflow-hidden bg-gray-200 animate-pulse">
              <img
                src={item.image_url}
                alt={item.name}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                onLoad={e => { e.currentTarget.parentElement.classList.remove('animate-pulse', 'bg-gray-200'); }}
                onError={e => { e.target.parentElement.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f9fafb;font-size:4rem">🍽️</div>`; }}
              />
              {/* Alt gradient overlay — başlık + fiyat resmin altına biner */}
              <div
                className="absolute inset-x-0 bottom-0 px-4 pt-12 pb-3 flex items-end justify-between gap-3"
                style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.15) 60%, transparent)' }}
              >
                <div className="flex-1 min-w-0">
                  {item.category_name && (
                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-white/80 mb-1">
                      {item.category_name}
                    </span>
                  )}
                  <h3 className="font-black text-xl leading-tight line-clamp-2 text-white drop-shadow-md" style={{ fontFamily: headingFont }}>
                    {item.name}
                  </h3>
                </div>
                {showPrice && (
                  <span
                    className="text-base font-black px-3 py-1.5 rounded-full text-white shrink-0 backdrop-blur-md shadow-lg"
                    style={{ background: accent, boxShadow: `0 4px 14px ${accent}66` }}
                  >
                    {formatPrice(item.price, lang)}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div
              className="relative w-full h-52 flex flex-col items-center justify-center"
              style={{ background: `linear-gradient(160deg, ${accent}, ${accent}aa)` }}
            >
              <span className="text-6xl mb-2 drop-shadow-md" aria-hidden="true">🍽️</span>
              <div className="absolute inset-x-0 bottom-0 px-4 pt-10 pb-3 flex items-end justify-between gap-3" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)' }}>
                <div className="flex-1 min-w-0">
                  {item.category_name && (
                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-white/80 mb-1">
                      {item.category_name}
                    </span>
                  )}
                  <h3 className="font-black text-xl leading-tight line-clamp-2 text-white drop-shadow-md" style={{ fontFamily: headingFont }}>
                    {item.name}
                  </h3>
                </div>
                {showPrice && (
                  <span className="text-base font-black px-3 py-1.5 rounded-full text-white shrink-0 backdrop-blur-md bg-black/40 shadow-lg">
                    {formatPrice(item.price, lang)}
                  </span>
                )}
              </div>
            </div>
          )}
        </button>
        {/* Alt blok: açıklama + buton (padding'li ayrı blok) */}
        <div className="px-4 pt-3 pb-4 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            {showDescription && item.description ? (
              <p className="text-sm line-clamp-2 leading-relaxed" style={{ color: 'var(--qm-text-muted, #6b7280)' }}>
                {item.description}
              </p>
            ) : (
              <span className="text-xs italic" style={{ color: 'var(--qm-text-muted, #9ca3af)' }}>
                {t('qrmenuTapForDetail') || 'Detay için dokun'}
              </span>
            )}
            <div className="mt-1">{renderAllergens(6, 'text-base')}</div>
          </div>
          {cartQty > 0 && !item.variants?.length ? (
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={onDecrement} aria-label={t('qrmenuDecrement') || 'Bir adet azalt'} className="w-10 h-10 rounded-full border-2 flex items-center justify-center text-lg font-bold active:scale-90 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current" style={{ borderColor: accent, color: accent }}><span aria-hidden="true">−</span></button>
              <span className="w-6 text-center text-base font-black" style={{ color: accent }} aria-live="polite">{cartQty}</span>
              <button onClick={onIncrement} aria-label={t('qrmenuIncrement') || 'Bir adet ekle'} className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-bold active:scale-90 transition-all shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current" style={{ backgroundColor: accent }}><span aria-hidden="true">+</span></button>
            </div>
          ) : (
            <button
              onClick={onAdd}
              aria-label={t('qrmenuAddToCart') || 'Ürünü sepete ekle'}
              className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white text-2xl font-bold transition-all active:scale-90 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
              style={{ backgroundColor: accent, boxShadow: `0 6px 16px ${accent}55` }}
            ><span aria-hidden="true">+</span></button>
          )}
        </div>
      </div>
    );
  }

  // ─── LIST (default): compact dense, divider-only, 64×64 image, sağ uçta yuvarlak buton ───
  // List'te variant'lar daha sessiz: border-color, sol-accent şerit, font tweaks
  const listImgRadius = cardVariant === 'neo-brutalist' || cardVariant === 'minimalist-mono' || cardVariant === 'editorial' || cardVariant === 'art-deco' ? 'rounded-none' : 'rounded-lg';
  return (
    <div
      className={`w-full flex items-center gap-3 py-3 px-1 transition-colors hover:bg-black/[0.02] relative ${decor.className}`}
      style={{
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 18%, transparent)',
        ...decor.style,
      }}
    >
      {decor.badge && (
        <span
          className="absolute -top-1 left-0 px-1.5 py-0 rounded text-[9px] font-black uppercase tracking-wider"
          style={decor.badge.style}
          aria-hidden="true"
        >{decor.badge.icon}</span>
      )}
      <button onClick={onDetail} className="flex-1 flex items-center gap-3 text-left active:opacity-80 transition-all min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[var(--color-primary,var(--qm-primary,#f97316))] rounded-lg">
        {hasImage ? (
          <div className={`w-16 h-16 flex-shrink-0 ${listImgRadius} overflow-hidden bg-gray-200 animate-pulse`} style={{ background: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 10%, transparent)' }}>
            <img
              src={item.image_url}
              alt={item.name}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
              onLoad={e => { e.currentTarget.parentElement.classList.remove('animate-pulse', 'bg-gray-200'); }}
              onError={e => { e.target.parentElement.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f3f4f6;font-size:1.5rem">🍽️</div>`; }}
            />
          </div>
        ) : (
          <div
            className={`w-16 h-16 flex-shrink-0 ${listImgRadius} flex items-center justify-center text-2xl`}
            style={{ background: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 10%, transparent)', color: accent }}
          >🍽️</div>
        )}
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="font-semibold text-sm leading-tight line-clamp-1 flex-1 min-w-0" style={{ color: 'var(--qm-text, #111827)', fontFamily: headingFont, ...(cardVariant === 'editorial' || cardVariant === 'art-deco' ? priceStyle : {}) }}>{item.name}</h3>
            {showPrice && (
              <span className="text-sm font-black shrink-0" style={{ color: accent, ...priceStyle }}>
                {formatPrice(item.price, lang)}
              </span>
            )}
          </div>
          {showDescription && item.description && (
            <p className="text-xs line-clamp-1 leading-snug" style={{ color: 'var(--qm-text-muted, #6b7280)' }}>{item.description}</p>
          )}
          {renderAllergens(5, 'text-xs')}
        </div>
      </button>

      {/* Sağ uçta yuvarlak 36×36 + buton (list imzası) */}
      <div className="shrink-0 pl-1">
        {cartQty > 0 && !item.variants?.length ? (
          <div className="flex items-center gap-1">
            <button
              onClick={onDecrement}
              aria-label={t('qrmenuDecrement') || 'Bir adet azalt'}
              className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold transition-all active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
              style={{ borderColor: accent, color: accent }}
            ><span aria-hidden="true">−</span></button>
            <span className="w-5 text-center text-sm font-black" style={{ color: accent }} aria-live="polite">{cartQty}</span>
            <button
              onClick={onIncrement}
              aria-label={t('qrmenuIncrement') || 'Bir adet ekle'}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold transition-all active:scale-90 shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
              style={{ backgroundColor: accent }}
            ><span aria-hidden="true">+</span></button>
          </div>
        ) : (
          <button
            onClick={onAdd}
            aria-label={t('qrmenuAddToCart') || 'Ürünü sepete ekle'}
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-lg font-bold shadow-md transition-all active:scale-90 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
            style={{ backgroundColor: accent, boxShadow: `0 3px 10px ${accent}55` }}
          ><span aria-hidden="true">+</span></button>
        )}
      </div>
    </div>
  );
}

// ─── Item detail + add to cart modal ─────────────────────────────────────────

function ItemModal({ item, accent, cart, onAdd, onClose }) {
  const { t, lang } = useLanguage();
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [note, setNote] = useState('');
  const panelRef = useRef(null);
  const titleId = useId();
  useFocusTrap(true, panelRef, { onEscape: onClose });

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (item.variants?.length) setSelectedVariant(item.variants[0]);
    return () => { document.body.style.overflow = ''; };
  }, [item]);

  const unitPrice = parseFloat(item.price || 0) + parseFloat(selectedVariant?.price_modifier || 0);

  return (
    <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        ref={panelRef}
        className="relative w-full max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--qm-surface, #ffffff)', fontFamily: 'var(--qm-font, system-ui)' }}
      >
        {item.image_url && (
          <div className="w-full h-52 relative" style={{ background: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 12%, transparent)' }}>
            <img src={item.image_url} alt={item.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>
        )}
        <div className="p-6 pb-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h2 id={titleId} className="font-black text-xl leading-tight" style={{ color: 'var(--qm-text, #111827)', fontFamily: 'var(--qm-font-heading, var(--qm-font, inherit))' }}>{item.name}</h2>
            <span className="text-2xl font-black flex-shrink-0" style={{ color: accent }}>
              {formatPrice(unitPrice, lang)}
            </span>
          </div>
          {item.category_name && (
            <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full mb-3" style={{ backgroundColor: `${accent}18`, color: accent }}>
              {item.category_name}
            </span>
          )}
          {item.description && (
            <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--qm-text-muted, #6b7280)' }}>{item.description}</p>
          )}

          {/* Allergens */}
          {(() => {
            const als = item.allergens
              ? (typeof item.allergens === 'string' ? JSON.parse(item.allergens) : item.allergens)
              : [];
            return als.length > 0 ? (
              <div className="mb-3 p-3 bg-orange-50 rounded-2xl border border-orange-100">
                <p className="text-xs font-bold text-orange-700 mb-2">⚠️ {t('qrmenuAllergens')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {als.map(key => {
                    const a = ALLERGEN_MAP[key];
                    return a ? (
                      <span key={key} className="flex items-center gap-1 bg-white border border-orange-200 text-orange-800 text-xs font-medium px-2 py-1 rounded-full">
                        <span>{a.icon}</span>
                        <span>{a.label}</span>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            ) : null;
          })()}

          {/* Variants */}
          {item.variants?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold mb-2" style={{ color: 'var(--qm-text-muted, #6b7280)' }}>{t('qrmenuOption')}</p>
              <div className="flex flex-wrap gap-2">
                {item.variants.map(v => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVariant(v)}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
                    style={selectedVariant?.id === v.id
                      ? { backgroundColor: accent, color: '#fff', borderColor: accent }
                      : { backgroundColor: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 10%, transparent)', color: 'var(--qm-text-muted, #6b7280)', borderColor: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 20%, transparent)' }}
                  >
                    {v.name}{v.price_modifier > 0 ? ` (+${formatPrice(v.price_modifier, lang)})` : ''}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Note */}
          <div className="mb-4">
            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--qm-text-muted, #6b7280)' }}>{t('qrmenuNoteLabel')}</p>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={t('qrmenuSpecialRequestPlaceholder')}
              aria-label={t('itemNotePlaceholder') || t('qrmenuNoteLabel') || 'Ürün için özel not'}
              className="w-full rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-current"
              style={{
                background: 'color-mix(in srgb, var(--qm-surface, #ffffff) 70%, transparent)',
                borderWidth: 1, borderStyle: 'solid',
                borderColor: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 25%, transparent)',
                color: 'var(--qm-text, #111827)',
              }}
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-2xl text-sm font-bold transition-all active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary,var(--qm-primary,#f97316))]"
              style={{
                background: 'color-mix(in srgb, var(--qm-text-muted, #6b7280) 12%, transparent)',
                color: 'var(--qm-text-muted, #6b7280)',
              }}
            >
              {t('qrmenuClose')}
            </button>
            <button
              onClick={() => onAdd({ ...item, note }, selectedVariant)}
              className="flex-1 py-3 rounded-2xl text-sm font-bold text-white transition-all active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
              style={{ backgroundColor: accent }}
            >
              {t('qrmenuAddToCart')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
